/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Dialog } from '@coursera/cds-core';

import DefineGoalScreen from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/DefineGoalScreen';
import DiscardScreen from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/DiscardScreen';
import EditLearnerListScreen from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/EditLearnerListScreen';
import GeneratingScreen from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/GeneratingScreen';
import LearnerListScreen from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/LearnerListScreen';
import ManualDraftScreen from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/ManualDraftScreen';
import MessageSentScreen from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/MessageSentScreen';
import MessageStepper from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/MessageStepper';
import ReviewAndSendScreen from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/ReviewAndSendScreen';
import type {
  AIMessageDraft,
  AIMessageScreen,
  AudienceDelta,
  AudienceFilterSpec,
  AudienceSelection,
  AudienceSource,
  Learner,
} from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import PreviewEmailScreen from 'bundles/enterprise-admin-messaging-v2/components/common/PreviewEmailScreen';
import { messagingDialogSize } from 'bundles/enterprise-admin-messaging-v2/components/common/dialogStyles';
import useAICohortFlow from 'bundles/enterprise-admin-messaging-v2/hooks/useAICohortFlow';
import useAIMessageModalTracking from 'bundles/enterprise-admin-messaging-v2/hooks/useAIMessageModalTracking';
import type { LearnerListAudience } from 'bundles/enterprise-admin-messaging-v2/hooks/useAudienceLearners';
import useAutoGenerate from 'bundles/enterprise-admin-messaging-v2/hooks/useAutoGenerate';
import useCohortPreview from 'bundles/enterprise-admin-messaging-v2/hooks/useCohortPreview';
import {
  buildClientUserFilter,
  getLegacyMessagePreset,
  getMessageCallToAction,
  useCreateSavedFilter,
} from 'bundles/enterprise-admin-messaging-v2/hooks/useCreateMessageFilter';
import useGenerateEmailContent from 'bundles/enterprise-admin-messaging-v2/hooks/useGenerateEmailContent';
import type { GenerateContextEntry } from 'bundles/enterprise-admin-messaging-v2/hooks/useGenerateEmailContent';
import useSendMessage from 'bundles/enterprise-admin-messaging-v2/hooks/useSendMessage';
import type { AnalyticsMeta } from 'bundles/enterprise-admin-messaging-v2/hooks/useSendMessage';
import { useRefreshSentMessages } from 'bundles/enterprise-admin-messaging-v2/hooks/useSentMessages';
import type { PreviewReturnScreen } from 'bundles/enterprise-admin-messaging-v2/utils/constants';
import { PREVIEW_RETURN_SCREEN, getCtaDisplayLabel } from 'bundles/enterprise-admin-messaging-v2/utils/constants';
import { MESSAGING_MODAL, SUGGESTION_ID_BY_ACTION_KEY } from 'bundles/enterprise-admin-messaging-v2/utils/eventing';
import { useEnterpriseAdminPermissions } from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsProvider';
import { useThirdPartyOrganization } from 'bundles/enterprise-admin/ThirdPartyOrganizationProvider';

import _t from 'i18n!nls/enterprise-admin-messaging-v2';

type Props = {
  open: boolean;
  initialPrompt: string;
  /**
   * When true, skip the Define screen on open and immediately generate from
   * `initialPrompt`, landing on the loading screen then Review and send.
   */
  autoGenerate?: boolean;
  /**
   * How the audience is resolved. For a `filter` source, the saved-filter id
   * is created when the admin reaches Review and send.
   */
  audienceSource?: AudienceSource;
  /** Whether the Sent screen offers the auto-send automation option (default true). */
  showAutomateScreen?: boolean;
  onClose: () => void;
  /** Fired once the message is successfully sent (used to mark a recommended action completed). */
  onSent?: () => void;
  /**
   * How long each of the two Generating loading stages runs. Exposed so
   * tests (and Storybook) can short-circuit the timer.
   */
  generatingStageDurationMs?: number;
};

// Mirror Dialog.HeadingGroup's horizontal padding so the stepper aligns with
// the title's left edge instead of receiving the wider Dialog.Content padding.
const stepperSlot = css`
  padding: 0 var(--cds-spacing-300);
`;

// Both learner screens need more vertical room than the shared content cap
// (93vh) so the table + pagination + actions all fit without scrolling.
// Composed on top of `messagingDialogSize` for both viewer and editor.
const dialogLearnerCap = css`
  .cds-Dialog-dialog {
    max-height: 95vh;
  }
`;

// Additional override applied only to the Learner list (viewer) so the
// modal stays a stable 872px regardless of how many rows the filter shows.
// The editor stays hug-content (with the 95vh cap above).
const dialogLearnerViewerHeight = css`
  .cds-Dialog-dialog {
    height: 872px;
  }
`;

const buildEmptyDraft = (): AIMessageDraft => {
  return {
    prompt: '',
    audienceSummary: '',
    audienceCount: 0,
    subject: '',
    message: '',
    ctaText: '',
    ctaUrl: '',
    autoSendEnabled: true,
    autoSendCadence: '15_days',
    learners: [],
    originalLearners: [],
    isManuallyEdited: false,
    contentSource: 'ai_generated',
    messageTone: 'none',
    promptStarterId: null,
  };
};

const titleForScreen = (screen: AIMessageScreen): string => {
  switch (screen) {
    case 'sent':
      return _t('Message sent successfully');
    case 'review':
      return _t('Review and send');
    case 'manual':
      return _t('Draft a message manually');
    case 'discard':
      return _t('Discard this message?');
    case 'preview':
      return _t('Preview and send');
    case 'learner-list':
      return _t('Learner list');
    case 'edit-learner-list':
      return _t('Add or remove learners');
    case 'define':
    case 'generating':
    default:
      return _t('Create a message');
  }
};

// Label for the audience pill on Define (table-selection entry points): the
// program name for a program-scope select-all, otherwise the learner count.
const getSelectionPillLabel = (selection: AudienceSelection): string => {
  if (selection.scope === 'program' && selection.isSelectAll && selection.programName) {
    return selection.programName;
  }
  return selection.count === 1 ? _t('1 learner') : _t('#{count} learners', { count: selection.count });
};

const getOrgIdFromSource = (source: AudienceSource | undefined): string | undefined => {
  if (source?.kind === 'filter') return source.spec.orgId;
  if (source?.kind === 'selection') return source.orgId;
  return undefined;
};

/**
 * Maps a filter-backed entry-point audienceType to the analytics prompt-starter
 * ID (same IDs used by the suggestion pills). Returns null for sources without
 * a matching ID (selection, generic AI).
 */
const getPromptStarterIdFromSource = (source: AudienceSource | undefined): string | null => {
  if (source?.kind !== 'filter') return null;
  // Partial: audiences without a single prompt-starter (e.g. ORG_ALL_JOINED,
  // shared by the recommended-actions messages that seed explicit prompts) are
  // omitted and resolve to null below.
  const mapping: Partial<Record<AudienceFilterSpec['audienceType'], keyof typeof SUGGESTION_ID_BY_ACTION_KEY>> = {
    INVITED_NOT_JOINED: 'REMIND_TO_JOIN',
    ORG_INVITED_NOT_JOINED: 'REMIND_TO_JOIN',
    JOINED_NOT_ENROLLED: 'REMIND_TO_ENROLL',
    ORG_INACTIVE_LEARNERS: 'MESSAGE_INACTIVE_LEARNERS',
    ORG_ENROLLED_LEARNERS: 'MESSAGE_ENROLLED_LEARNERS',
    ORG_RECENT_COMPLETERS: 'SEND_ACCOLADES',
    ORG_INCOMPLETE_IN_COURSE: 'REMINDER_TO_COMPLETE_COURSE',
  };
  const actionKey = mapping[source.spec.audienceType];
  return actionKey ? SUGGESTION_ID_BY_ACTION_KEY[actionKey] : null;
};

/**
 * Top-level dialog. Owns the active sub-screen + draft state, and dispatches
 * to the four sub-screens. Reset every time the modal re-opens so a fresh
 * `initialPrompt` always starts from Step 1.
 */
const AIMessageModal = ({
  open,
  initialPrompt,
  autoGenerate = false,
  audienceSource: audienceSourceProp,
  showAutomateScreen = true,
  onClose,
  onSent,
  generatingStageDurationMs,
}: Props) => {
  const [activeScreen, setActiveScreen] = React.useState<AIMessageScreen>('define');
  // Held in state (not read straight from the prop) so the Define-screen pill
  // can drop a table-selection audience — ✕ clears it and the modal reverts to
  // the generic AI flow. Re-synced from the prop on each open.
  const [audienceSource, setAudienceSource] = React.useState(audienceSourceProp);
  const [draft, setDraft] = React.useState<AIMessageDraft>(() => buildEmptyDraft());
  // Preview can be reached from Review (AI flow) or Manual; its Back button
  // returns to whichever screen opened it.
  const [previewReturnScreen, setPreviewReturnScreen] = React.useState<PreviewReturnScreen>(
    PREVIEW_RETURN_SCREEN.REVIEW
  );
  // Closing mid-draft swaps to the 'discard' confirmation screen; this remembers
  // which screen to restore if the admin chooses "Keep editing".
  const [screenBeforeDiscard, setScreenBeforeDiscard] = React.useState<AIMessageScreen>('define');
  // A single, generic error banner on the Define screen, shown whenever generation
  // fails. Per product: one banner for every failure mode (vague prompt, off-topic,
  // server error, …) — never more than one.
  const [hasGenerateError, setHasGenerateError] = React.useState(false);

  // Surfaced on Review when the send fails, so the admin stays put and can retry.
  const [sendError, setSendError] = React.useState<string | null>(null);

  const { generate } = useGenerateEmailContent();
  const { sendMessage, previewMessage, isSending } = useSendMessage();
  const { trackCloseModal, trackSend } = useAIMessageModalTracking();
  const createSavedFilter = useCreateSavedFilter();
  // Org the manual-CSV flow uploads + sends under (it has no `audienceSource` to
  // carry an orgId). Resolved from the admin-dashboard org context.
  const orgId = useThirdPartyOrganization()?.id ?? '';
  // Programs the sending admin has access to, for the manual cohort's
  // `filterParams.programIds`. Org admins have org-wide access → no scoping.
  const { getIsOrganizationDomainAdmin, getAllProgramIdsWithPermission } = useEnterpriseAdminPermissions();
  // Invalidates the sent-messages list cache so the table reloads with the new
  // message once the admin returns to it. A no-op on entry points without that
  // table (e.g. admin-home), where there's no list mounted to refresh.
  const refreshSentMessages = useRefreshSentMessages();

  // Saved filterId for a learners-table `selection`, created lazily on the first
  // preview or send and cached so the two reuse the same filter (the selection's
  // audience is fixed, so it never needs re-creating within one modal session).
  // The funnel `filter` source creates its filterId on open (`draft.filterId`).
  const selectionFilterIdRef = React.useRef<string | undefined>(undefined);

  const updateDraft = React.useCallback((partial: Partial<AIMessageDraft>) => {
    setDraft((previous) => ({ ...previous, ...partial }));
  }, []);

  // AI cohort flow: owns creation, status routing, draft updates, and send-args
  // resolution for the generic AI compose path (no pre-resolved audience).
  const cohortFlow = useAICohortFlow({
    open,
    prompt: draft.prompt,
    orgId,
    updateDraft,
  });

  const cohortPreview = useCohortPreview({ draft, cohortFlow, previewMessage });

  // Live mirror of `audienceSource` for `runGenerate` to read when its async
  // generate resolves. The auto-generate flow fires `runGenerate` on the first
  // open render — before the reset-on-open effect copies the source prop into
  // state — so a closed-over `audienceSource` would be stale (undefined) and
  // wrongly apply the mock audience over the real resolved count. The ref always
  // holds the latest value by the time the (slow) generate call settles.
  const audienceSourceRef = React.useRef(audienceSource);
  React.useEffect(() => {
    audienceSourceRef.current = audienceSource;
  }, [audienceSource]);

  // Build the additionalContext entries for the generate API from the current
  // audience source. Always reads the ref so it reflects the latest value by the
  // time the (possibly delayed) generation call fires.
  const buildAdditionalContext = React.useCallback((): GenerateContextEntry[] | undefined => {
    const sourceOrgId = getOrgIdFromSource(audienceSourceRef.current);
    const resolvedOrgId = sourceOrgId || orgId;
    if (!resolvedOrgId) return undefined;
    return [{ key: 'additionalContext.enterprise_organization_id', value: resolvedOrgId }];
  }, [orgId]);

  // Generate the email copy from a prompt, routing to Review on success or back
  // to Define with the single error banner on any failure. Takes the prompt
  // explicitly so callers (the Define button and auto-generate-on-open) don't
  // depend on the async draft state having settled first.
  const runGenerate = React.useCallback(
    async (prompt: string) => {
      setHasGenerateError(false);
      setActiveScreen('generating');

      const currentAudienceSource = audienceSourceRef.current;
      const hasResolvedAudience =
        currentAudienceSource?.kind === 'filter' || currentAudienceSource?.kind === 'selection';

      // Fire email generation and cohort creation in parallel. The cohort call
      // is only needed for the generic AI flow (no pre-resolved audience).
      const generatePromise = generate(prompt, buildAdditionalContext());
      if (!hasResolvedAudience) {
        cohortFlow.createCohort();
      }

      const result = await generatePromise;
      if (result.status === 'success') {
        const { subject, body, callToAction, targetAudience, isEnrollmentSpecific } = result.content;

        let resolvedCtaText: string;
        if (currentAudienceSource?.kind === 'filter') {
          resolvedCtaText = getCtaDisplayLabel(getMessageCallToAction(currentAudienceSource.spec.audienceType));
        } else if (currentAudienceSource?.kind === 'selection') {
          resolvedCtaText = getCtaDisplayLabel('FIND_A_COURSE');
        } else {
          resolvedCtaText = getCtaDisplayLabel(callToAction);
        }

        setDraft((previous) => ({
          ...previous,
          subject,
          message: body,
          ctaText: resolvedCtaText,
          isEnrollmentSpecific,
          originalAiMessage: body,
          targetAudience: targetAudience || '',
        }));
        setActiveScreen('review');
      } else {
        setHasGenerateError(true);
        setActiveScreen('define');
      }
    },
    [generate, buildAdditionalContext, cohortFlow]
  );

  const handleGenerate = React.useCallback(() => runGenerate(draft.prompt), [runGenerate, draft.prompt]);

  // If the cohort flow fails (empty result or API error) while the modal has
  // already advanced past Define (e.g. email generation resolved first), snap
  // back to Define so the admin sees the error banner.
  React.useEffect(() => {
    if (
      (cohortFlow.status === 'error' || cohortFlow.status === 'disambiguation') &&
      (activeScreen === 'review' || activeScreen === 'generating')
    ) {
      setActiveScreen('define');
    }
  }, [cohortFlow.status, activeScreen]);

  // Derive the audience summary from the cohort count + the AI-generated
  // targetAudience label once both the cohort and email generation resolve.
  React.useEffect(() => {
    if (cohortFlow.status !== 'success') return;
    const audience = draft.targetAudience?.toLowerCase();
    const count = draft.audienceCount;
    if (count > 0 && audience) {
      updateDraft({ audienceSummary: _t('#{count} #{audience}', { count, audience }) });
    } else if (count > 0) {
      updateDraft({ audienceSummary: _t('#{count} learners', { count }) });
    }
    // Only re-derive when the cohort lands or the targetAudience changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cohortFlow.status, draft.targetAudience, draft.audienceCount]);

  // Reset the modal to a clean state whenever it opens. Using `open` as the
  // trigger keeps the previous draft visible while the close animation plays.
  // The auto-generate flow (trigger generation + resolve the audience) is owned
  // by `useAutoGenerate` below; here we only land the manual flow on Define.
  React.useEffect(() => {
    if (!open) return;
    setHasGenerateError(false);
    setSendError(null);
    selectionFilterIdRef.current = undefined;
    setAudienceSource(audienceSourceProp);
    // Sync the ref eagerly so `buildAdditionalContext` (called by the
    // auto-generate flow in the same effect cycle) reads the fresh source
    // before the state update triggers a re-render + ref-sync effect.
    audienceSourceRef.current = audienceSourceProp;
    setDraft({
      ...buildEmptyDraft(),
      prompt: initialPrompt,
      promptStarterId: getPromptStarterIdFromSource(audienceSourceProp),
    });
    if (!autoGenerate) {
      setActiveScreen('define');
    }
  }, [open, initialPrompt, autoGenerate, audienceSourceProp]);

  // Tear down all draft/orchestration state once the modal closes so nothing
  // leaks into the next open (the dialog also unmounts below, which clears the
  // learner-list hooks' debounce timers + in-flight fetches via their effect
  // cleanups). The reset-on-open effect re-seeds the prompt/source for the next run.
  React.useEffect(() => {
    if (open) return;
    setActiveScreen('define');
    setAudienceSource(undefined);
    selectionFilterIdRef.current = undefined;
    setDraft(buildEmptyDraft());
    setPreviewReturnScreen(PREVIEW_RETURN_SCREEN.REVIEW);
    setScreenBeforeDiscard('define');
    setHasGenerateError(false);
    setSendError(null);
  }, [open]);

  // The audience the count + learner-list resolve from, as a client `UserFilter`:
  // a funnel `filter` source builds it from the semantic spec; a learners-table
  // `selection` hands its dedicated filter in directly. Anything else (generic AI
  // flow / removed pill) → undefined, so count is skipped and the list screens
  // fall back to the in-memory cohort. Memoized so the fetch isn't re-run per render.
  const learnerListAudience: LearnerListAudience | undefined = React.useMemo(() => {
    if (audienceSource?.kind === 'filter') {
      return { orgId: audienceSource.spec.orgId, userFilter: buildClientUserFilter(audienceSource.spec) };
    }
    if (audienceSource?.kind === 'selection') {
      return {
        orgId: audienceSource.orgId,
        userFilter: audienceSource.userFilter,
        userIdentifiers: audienceSource.userIdentifiers,
      };
    }
    return undefined;
  }, [audienceSource]);

  // Owns the auto-generate entry-point flow: kicks off generation from the known
  // prompt on open and resolves the saved filter + audience count in parallel.
  // Declared after the reset effect so the draft is reset before generation begins.
  const { isAudienceLoading } = useAutoGenerate({
    open,
    autoGenerate,
    initialPrompt,
    audienceSource,
    learnerListAudience,
    runGenerate,
    updateDraft,
  });

  // Resolve the orgId / filterId / preset / CTA the draft+publish (and the
  // preview render) need, per audience source. The funnel `filter` source
  // returns its open-time `draft.filterId` verbatim (an empty id surfaces the
  // "audience not ready" error downstream). The learners-table `selection`
  // creates its saved filter lazily and caches it, so a preview followed by a
  // send reuses the one filter; null means that creation failed.
  const resolveSendArgs = React.useCallback(async () => {
    // The CTA button is optional across every entry point: it follows the
    // Review-screen "Call to action" label. An empty label means no button
    // (`DO_NOT_INCLUDE`) regardless of audience; a label sends the
    // audience-appropriate CTA below.
    const hasCta = draft.ctaText.trim().length > 0;
    if (audienceSource?.kind === 'filter') {
      const { spec } = audienceSource;
      return {
        orgId: spec.orgId,
        filterId: draft.filterId ?? '',
        preset: getLegacyMessagePreset(spec.audienceType),
        callToAction: hasCta ? getMessageCallToAction(spec.audienceType) : 'DO_NOT_INCLUDE',
        // Program-scoped specs carry a programId; pass it so the JOIN_PROGRAM
        // CTA link resolves. Org-wide specs have no single program → omitted.
        programIds: 'programId' in spec ? [spec.programId] : undefined,
        audienceDelta: draft.audienceDelta,
      };
    }
    if (audienceSource?.kind === 'selection') {
      let filterId = selectionFilterIdRef.current;
      if (!filterId) {
        filterId = await createSavedFilter(
          audienceSource.orgId,
          audienceSource.userFilter,
          audienceSource.userIdentifiers
        );
        selectionFilterIdRef.current = filterId;
      }
      if (!filterId) return null;
      // Custom learners-table message (program- or org-scoped): with a CTA label
      // send the legacy custom-preset CTA (`FIND_A_COURSE`, the only program-style
      // CTA valid for `custom`); with none, no button.
      const callToAction = hasCta ? 'FIND_A_COURSE' : 'DO_NOT_INCLUDE';
      return {
        orgId: audienceSource.orgId,
        filterId,
        preset: 'custom',
        callToAction,
        audienceDelta: draft.audienceDelta,
      };
    }
    // Manual-CSV draft (no `audienceSource`): the audience resolves server-side
    // from the uploaded cohort, so send the S3 object instead of a filter. Same
    // `custom` preset + CTA mapping as the learners-table selection.
    if (draft.manualCsvS3Object) {
      // Org admins have org-wide access (no program scoping); program admins send
      // the programs they can view so the BE scopes the cohort to their access.
      const programIds = getIsOrganizationDomainAdmin()
        ? []
        : getAllProgramIdsWithPermission('DEFAULT_SUBDOMAIN', 'VIEW');
      return {
        orgId,
        preset: 'custom',
        callToAction: hasCta ? 'FIND_A_COURSE' : 'DO_NOT_INCLUDE',
        programIds,
        manualCohort: { s3Object: draft.manualCsvS3Object },
      };
    }
    return null;
  }, [
    audienceSource,
    draft.filterId,
    draft.ctaText,
    draft.audienceDelta,
    draft.manualCsvS3Object,
    orgId,
    createSavedFilter,
    getIsOrganizationDomainAdmin,
    getAllProgramIdsWithPermission,
  ]);

  // Render the preview by creating a draft and asking the API for its HTML — the
  // exact email the backend would send. Throws so the preview screen can show its
  // error state. Wired for backend-audience sources (filter / selection / manual
  // CSV); the generic AI-compose flow falls back to the local mock compile.
  const handleGetPreviewHtml = React.useCallback(async (): Promise<string> => {
    const sendArgs = await resolveSendArgs();
    if (!sendArgs) throw new Error('Preview is unavailable for this audience.');
    const result = await previewMessage({ draft, ...sendArgs });
    if (result.status !== 'success') throw new Error(result.error);
    return result.html;
  }, [resolveSendArgs, previewMessage, draft]);

  // Send fires the generic marker (`confirm_send`) on click AND the dedicated
  // `send_enterprise_admin_message` (carrying cohortSource) once the message is
  // actually sent. Both markers + the cohortSource derivation live in
  // useAIMessageModalTracking.
  const handleSend = React.useCallback(async () => {
    // Fires confirm_send now; returns a callback to fire the dedicated send
    // marker once the send actually completes.
    const markSent = trackSend({
      activeScreen,
      previewReturnScreen,
      isManuallyEdited: draft.isManuallyEdited,
      audienceSourceKind: audienceSource?.kind,
    });

    // Derive contentSource at send time: if the flow is manual, it's 'manual';
    // if AI-generated and the body was modified (direct edit or tone rewrite),
    // it's 'ai_edited'; otherwise 'ai_generated'.
    let resolvedContentSource = draft.contentSource;
    if (resolvedContentSource === 'ai_generated') {
      const bodyEdited = draft.originalAiMessage != null && draft.message !== draft.originalAiMessage;
      const toneApplied = draft.messageTone !== 'none';
      if (bodyEdited || toneApplied) {
        resolvedContentSource = 'ai_edited';
      }
    }

    const analyticsMeta: AnalyticsMeta = {
      triggerKind: 'ONE_TIME',
      contentSource: resolvedContentSource,
      messageTone: draft.messageTone,
      promptStarterId: draft.promptStarterId ?? '',
      cohortEdited: draft.isManuallyEdited,
      isResend: false,
    };

    // Backend-audience flows (funnel reminders, learners-table selection, and the
    // manual-CSV cohort): resolve the audience, then re-create + publish + send the
    // draft from the latest copy. The dedicated send marker is gated on success.
    const hasBackendAudience =
      audienceSource?.kind === 'filter' || audienceSource?.kind === 'selection' || Boolean(draft.manualCsvS3Object);
    if (hasBackendAudience) {
      setSendError(null);
      const sendArgs = await resolveSendArgs();
      if (!sendArgs) {
        setSendError(_t('Something went wrong preparing the audience. Please try again.'));
        return;
      }
      const result = await sendMessage({ draft, ...sendArgs, analyticsMeta });
      if (result.status === 'success') {
        markSent();
        // A real message was sent — refresh the sent-messages list so it reloads
        // with the new message when the admin returns to the table.
        refreshSentMessages();
        // Let recommended-action entry points mark their action completed.
        onSent?.();
        setActiveScreen('sent');
      } else {
        setSendError(result.error);
      }
      return;
    }
    // Generic AI compose (no resolved audience) completes via the cohort
    // send path: queryType ENTERPRISE_DYNAMIC_COHORT_RESOLUTION with cohortId.
    const cohortSendArgs = cohortFlow.resolveSendArgs(draft.ctaText, draft.audienceDelta);
    if (cohortSendArgs) {
      setSendError(null);
      const result = await sendMessage({ draft, ...cohortSendArgs, analyticsMeta });
      if (result.status === 'success') {
        markSent();
        refreshSentMessages();
        setActiveScreen('sent');
      } else {
        setSendError(result.error);
      }
    } else {
      setSendError(_t('The audience is not ready yet. Please wait for cohort creation to complete.'));
    }
  }, [
    activeScreen,
    previewReturnScreen,
    draft,
    audienceSource,
    sendMessage,
    resolveSendArgs,
    trackSend,
    refreshSentMessages,
    cohortFlow,
    onSent,
  ]);

  // Shared by Review's "Back" and Manual's "Draft with AI". Only Review's Back is
  // a dismiss of Step 2 → close_modal; Manual's switch already fires its own
  // `use_ai_prompt` marker, so it must not also emit a Step 2 close.
  const handleBackToDefine = React.useCallback<() => void>(() => {
    if (activeScreen === 'review') {
      trackCloseModal(MESSAGING_MODAL.CREATE_STEP2);
    }
    // Leaving the manual flow for the AI flow: drop the uploaded-CSV audience so
    // the AI send/preview doesn't inherit the stale manual cohort (the presence
    // of `manualCsvS3Object` is what routes a send through MANUAL_COHORT_RESOLUTION).
    if (draft.manualCsvS3Object) {
      updateDraft({ manualCsvS3Object: undefined, audienceFileName: undefined, audienceCount: 0 });
    }
    setActiveScreen('define');
  }, [activeScreen, trackCloseModal, draft.manualCsvS3Object, updateDraft]);

  const handleDraftManually = React.useCallback(() => {
    updateDraft({ contentSource: 'manual' });
    setActiveScreen('manual');
  }, [updateDraft]);

  const handlePreviewEmail = React.useCallback(() => {
    setPreviewReturnScreen(PREVIEW_RETURN_SCREEN.REVIEW);
    setActiveScreen('preview');
  }, []);

  const handlePreviewManually = React.useCallback(() => {
    setPreviewReturnScreen(PREVIEW_RETURN_SCREEN.MANUAL);
    setActiveScreen('preview');
  }, []);

  const handleViewLearnerList = React.useCallback(() => {
    setActiveScreen('learner-list');
  }, []);

  const handleBackToReview = React.useCallback<() => void>(() => {
    // Leaving the learner list (viewer) back to Review.
    trackCloseModal(MESSAGING_MODAL.LEARNER_LIST);
    setActiveScreen('review');
  }, [trackCloseModal]);

  const handleEditLearnerList = React.useCallback(() => {
    setActiveScreen('edit-learner-list');
  }, []);

  const handleSaveLearnerList = React.useCallback<(nextLearners: Learner[]) => void>(
    (nextLearners) => {
      // Save closes the editor; the screen's own `save_learners` marker already
      // fired. Keep audienceCount in sync with the edited cohort — the Sent
      // screen reads draft.audienceCount, and Review shows learners.length.
      trackCloseModal(MESSAGING_MODAL.EDIT_LEARNERS);
      updateDraft({ learners: nextLearners, audienceCount: nextLearners.length, isManuallyEdited: true });
      setActiveScreen('review');
    },
    [trackCloseModal, updateDraft]
  );

  // Filter-flow editor: stash the add/remove delta on the draft. The base
  // audienceCount stays the cohort size; Review derives the effective count
  // from it + the delta. At send time, the delta is passed as
  // `enterpriseCohortContext.cohortOverride` so the BE resolves the final list.
  const handleSaveLearnerDelta = React.useCallback(
    (delta: AudienceDelta) => {
      const isEdited = delta.removedIds.length > 0 || delta.addedIds.length > 0;
      updateDraft({ audienceDelta: isEdited ? delta : undefined, isManuallyEdited: isEdited });
      setActiveScreen('review');
    },
    [updateDraft]
  );

  const handleResetLearnerList = React.useCallback<() => void>(() => {
    // Reset closes the editor (back to the viewer); the screen's own
    // `reset_learner_list` marker already fired.
    trackCloseModal(MESSAGING_MODAL.EDIT_LEARNERS);
    setDraft((previous) => ({
      ...previous,
      learners: previous.originalLearners,
      audienceCount: previous.originalLearners.length,
      audienceDelta: undefined,
      isManuallyEdited: false,
    }));
    setActiveScreen('learner-list');
  }, [trackCloseModal]);
  const handleBackFromPreview = React.useCallback<() => void>(() => {
    trackCloseModal(MESSAGING_MODAL.PREVIEW_EMAIL);
    setActiveScreen(previewReturnScreen);
  }, [previewReturnScreen, trackCloseModal]);

  // Every dismiss path (X, Esc, overlay, Cancel) routes through here. The Sent
  // screen has nothing left to lose, so it closes immediately (close its modal);
  // on the discard screen itself another dismiss means "keep editing"; every
  // other screen swaps to the discard confirmation first (its view fires on
  // mount).
  const handleRequestClose = React.useCallback<() => void>(() => {
    if (activeScreen === 'sent') {
      trackCloseModal(MESSAGING_MODAL.SEND_SUCCESS);
      onClose();
    } else if (activeScreen === 'discard') {
      trackCloseModal(MESSAGING_MODAL.CONFIRM_DISCARD);
      setActiveScreen(screenBeforeDiscard);
    } else {
      setScreenBeforeDiscard(activeScreen);
      setActiveScreen('discard');
    }
  }, [activeScreen, screenBeforeDiscard, onClose, trackCloseModal]);

  const handleKeepEditing = React.useCallback<() => void>(() => {
    trackCloseModal(MESSAGING_MODAL.CONFIRM_DISCARD);
    setActiveScreen(screenBeforeDiscard);
  }, [screenBeforeDiscard, trackCloseModal]);

  // Unmount the dialog entirely when closed so the modal title doesn't leak
  // into the document and the orchestrator state resets fresh on the next
  // open.
  if (!open) {
    return null;
  }

  let body: React.ReactNode = null;
  switch (activeScreen) {
    case 'define':
      body = (
        <DefineGoalScreen
          prompt={draft.prompt}
          onPromptChange={(value) => updateDraft({ prompt: value })}
          onGenerate={handleGenerate}
          onCancel={handleRequestClose}
          onDraftManually={handleDraftManually}
          onSuggestionSelect={(key) => updateDraft({ promptStarterId: key })}
          showError={hasGenerateError || cohortFlow.status === 'disambiguation' || cohortFlow.status === 'error'}
          onDismissError={() => {
            setHasGenerateError(false);
            cohortFlow.dismissError();
          }}
          errorMessage={cohortFlow.errorMessage ?? undefined}
          errorTitle={
            cohortFlow.status === 'error' || cohortFlow.status === 'disambiguation'
              ? _t("Couldn't generate the learner cohort")
              : undefined
          }
          audienceLabel={audienceSource?.kind === 'selection' ? getSelectionPillLabel(audienceSource) : undefined}
          onRemoveAudience={() => setAudienceSource(undefined)}
        />
      );
      break;
    case 'generating':
      body = <GeneratingScreen stageDurationMs={generatingStageDurationMs} />;
      break;
    case 'review':
      body = (
        <ReviewAndSendScreen
          draft={draft}
          updateDraft={updateDraft}
          onSend={handleSend}
          onBack={handleBackToDefine}
          onPreviewEmail={handlePreviewEmail}
          onViewLearnerList={handleViewLearnerList}
          audienceLoading={isAudienceLoading || cohortFlow.isCreating}
          sending={isSending}
          sendError={sendError}
        />
      );
      break;
    case 'learner-list':
      body = (
        <LearnerListScreen
          learners={draft.learners}
          audience={learnerListAudience}
          cohortAudience={cohortFlow.cohortAudience}
          onEdit={handleEditLearnerList}
          onBack={handleBackToReview}
        />
      );
      break;
    case 'edit-learner-list':
      body = (
        <EditLearnerListScreen
          initialLearners={draft.learners}
          audience={learnerListAudience}
          cohortAudience={cohortFlow.cohortAudience}
          initialDelta={draft.audienceDelta}
          onSave={handleSaveLearnerList}
          onSaveDelta={handleSaveLearnerDelta}
          onResetToOriginal={handleResetLearnerList}
        />
      );
      break;
    case 'manual':
      body = (
        <ManualDraftScreen
          draft={draft}
          updateDraft={updateDraft}
          onSend={handleSend}
          onDraftWithAI={handleBackToDefine}
          onPreviewEmail={handlePreviewManually}
          organizationId={orgId}
          sending={isSending}
          sendError={sendError}
        />
      );
      break;
    case 'preview': {
      const hasBackendAudience =
        audienceSource?.kind === 'filter' || audienceSource?.kind === 'selection' || Boolean(draft.manualCsvS3Object);

      let previewFetcher: (() => Promise<string>) | undefined;
      if (hasBackendAudience) {
        previewFetcher = handleGetPreviewHtml;
      } else if (cohortPreview.isReady) {
        previewFetcher = cohortPreview.fetchPreviewHtml;
      }

      body = (
        <PreviewEmailScreen
          draft={draft}
          onSend={handleSend}
          onBack={handleBackFromPreview}
          sending={isSending}
          sendError={sendError}
          fetchPreviewHtml={previewFetcher}
        />
      );
      break;
    }
    case 'sent':
      body = (
        <MessageSentScreen draft={draft} updateDraft={updateDraft} onDone={onClose} showAutomate={showAutomateScreen} />
      );
      break;
    case 'discard':
      body = <DiscardScreen onDiscard={onClose} onKeepEditing={handleKeepEditing} />;
      break;
    default:
      body = null;
  }

  const isLearnerScreen = activeScreen === 'learner-list' || activeScreen === 'edit-learner-list';
  const isLearnerListViewer = activeScreen === 'learner-list';

  return (
    <Dialog
      open={open}
      onClose={handleRequestClose}
      width="large"
      css={[messagingDialogSize, isLearnerScreen && dialogLearnerCap, isLearnerListViewer && dialogLearnerViewerHeight]}
      data-testid="ai-message-modal"
    >
      <Dialog.HeadingGroup>{titleForScreen(activeScreen)}</Dialog.HeadingGroup>
      {(activeScreen === 'define' || activeScreen === 'review') && (
        <div css={stepperSlot}>
          <MessageStepper activeStep={activeScreen === 'define' ? 1 : 2} />
        </div>
      )}
      {body}
    </Dialog>
  );
};

export default AIMessageModal;
